import { CommissionTier } from '../types/commissionTier';

export function calculateTierCommission(precio: number, porcentaje: number): number {
  const p = Number(precio) || 0;
  const pct = Number(porcentaje) || 0;
  // If percentage was entered as decimal (e.g. 0.03 instead of 3), handle gracefully
  const normalizedPct = pct <= 0.25 && pct > 0 ? pct * 100 : pct;
  return Math.round(p * (normalizedPct / 100));
}

export function normalizeCommissionTiers(rawTiers: any): CommissionTier[] {
  if (!Array.isArray(rawTiers)) return [];
  return rawTiers
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => {
      const precio = Number(item.precio) || 0;
      let porcentaje = Number(item.porcentaje) || 0;
      // If entered as rate fraction like 0.03 instead of 3%, normalize to percentage (3)
      if (porcentaje > 0 && porcentaje <= 0.25) {
        porcentaje = Number((porcentaje * 100).toFixed(3));
      }
      const valor_comision = item.valor_comision != null
        ? Number(item.valor_comision)
        : calculateTierCommission(precio, porcentaje);

      return {
        id: item.id || `tier-${index + 1}-${Date.now()}`,
        porcentaje,
        precio,
        valor_comision,
        descripcion: item.descripcion || '',
      };
    });
}

export function generateDefaultCommissionTiers(basePrice: number, baseRate: number): CommissionTier[] {
  const price = Number(basePrice) || 0;
  // baseRate in the system might be 0.03 or 3. If <= 0.25, it's a fraction (0.03 -> 3%)
  const ratePct = baseRate > 0 && baseRate <= 0.25 ? Number((baseRate * 100).toFixed(2)) : (baseRate || 3);
  if (price <= 0) {
    return [
      { id: 'tier-1', porcentaje: ratePct, precio: 0, valor_comision: 0, descripcion: 'Precio Lista' }
    ];
  }

  // Generate 3 standard tiers: 5% discount price, 100% list price, and negotiable lower price
  const discounts = [
    { label: 'Oferta con Descuento (-5%)', factor: 0.95, pctOffset: 0 },
    { label: 'Precio de Lista', factor: 1.0, pctOffset: 0 },
    { label: 'Venta con Comisión Especial (+0.5%)', factor: 1.0, pctOffset: 0.5 },
  ];

  return discounts.map((d, idx) => {
    const tierPrice = Math.round(price * d.factor);
    const tierPct = Number((ratePct + d.pctOffset).toFixed(2));
    return {
      id: `tier-${idx + 1}`,
      porcentaje: tierPct,
      precio: tierPrice,
      valor_comision: calculateTierCommission(tierPrice, tierPct),
      descripcion: d.label,
    };
  });
}

export function sortCommissionTiers(tiers: CommissionTier[]): CommissionTier[] {
  return [...tiers].sort((a, b) => a.precio - b.precio);
}

export function combineInitialAndExtraTiers(
  basePrice: number,
  baseRate: number,
  baseValue: number,
  extraTiers?: CommissionTier[] | null
): CommissionTier[] {
  const price = Number(basePrice) || 0;
  const ratePct = baseRate > 0 && baseRate <= 0.25 ? Number((baseRate * 100).toFixed(2)) : (baseRate || 3);
  const commVal = baseValue > 0 ? baseValue : calculateTierCommission(price, ratePct);

  const initialTier: CommissionTier = {
    id: 'initial-base-tier',
    precio: price,
    porcentaje: ratePct,
    valor_comision: commVal,
    descripcion: 'Comisión Inicial (Lista)',
    es_inicial: true,
  };

  const normalized = normalizeCommissionTiers(extraTiers);
  if (normalized.length === 0) {
    return [initialTier];
  }

  // Filter out any extra tier that exactly duplicates the initial price and percentage
  const filteredExtras = normalized.filter(
    (t) => !(Math.abs(t.precio - price) < 1 && Math.abs(t.porcentaje - ratePct) < 0.05)
  );

  return [initialTier, ...filteredExtras];
}
